import { fg } from '@atlaskit/platform-feature-flags';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { loomFoldersSearchQuery } from '@atlassian/search-client/loom/folder-search';
import { cleanExcerpt } from '@atlassian/search-common/search-result-utils';

import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type {
	SelectFilterMonad,
	SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import {
	ProductKeys,
	type ProductKeys as ProductKey,
} from '../../../../../common/constants/products';
import type { SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

const DEFAULT_LOOM_FOLDER_INITIAL_LIMIT = 15;
const DEFAULT_LOOM_FOLDER_LOOKUP_LIMIT = 15;
/** Max concurrent AGG `loom_foldersSearch` calls (1 default + N per selected space share this pool). */
const LOOM_FOLDER_AGG_CONCURRENCY = 5;

async function runWithConcurrencyLimit<T>(
	tasks: Array<() => Promise<T>>,
	concurrency: number,
): Promise<Array<T | undefined>> {
	if (tasks.length === 0) {
		return [];
	}

	const safeConcurrency = Math.max(1, Math.floor(concurrency));
	const results: Array<T | undefined> = new Array(tasks.length);
	let nextIndex = 0;

	const worker = async (): Promise<void> => {
		for (;;) {
			const index = nextIndex++;
			if (index >= tasks.length) {
				return;
			}
			const [outcome] = await Promise.allSettled([tasks[index]()]);
			results[index] = outcome.status === 'fulfilled' ? outcome.value : undefined;
		}
	};

	const workerCount = Math.min(safeConcurrency, tasks.length);
	await Promise.allSettled(Array.from({ length: workerCount }, () => worker()));
	return results;
}

const isLoomFolderProduct = (product?: string) =>
	product === ProductKeys.Loom && fg('loom-rovo-search');

const getSelectedSpaceAris = (config: CloudConfig): string[] =>
	Array.isArray(config.initialSpaceARIs) ? config.initialSpaceARIs.filter(Boolean) : [];

const mapFolderNodeToOption = (folder: {
	id: string;
	name: string;
	parentSpaceId?: string | null;
}): SelectFilterOption => ({
	trackingKey: folder.id,
	value: folder.id,
	label: cleanExcerpt(folder.name),
	queryParamValue: folder.id,
	product: ProductKeys.Loom,
	isDefault: false,
	isSelected: false,
	isSuggested: true,
	...(folder.parentSpaceId ? { metadata: { parentSpaceId: folder.parentSpaceId } } : {}),
});

async function fetchLoomFolderOptions(
	config: CloudConfig,
	{
		query = '',
		limit = DEFAULT_LOOM_FOLDER_INITIAL_LIMIT,
	}: { query?: string; limit?: number } = {},
): Promise<SelectFilterOption[]> {
	if (!isLoomFolderProduct(config.product)) {
		return [];
	}

	const cloudId = config.cloudId;
	if (!cloudId) {
		return [];
	}

	const spaceAris = getSelectedSpaceAris(config);

	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});

	const queryVariables = {
		siteId: cloudId,
		query: query || undefined,
		limit,
	};

	// Workspace / personal default folders (Personal Library, Archived, …) require no spaceId.
	// Run all folder fetches with bounded concurrency so many selected Loom spaces do not fan out
	// unbounded parallel AGG calls (see `LOOM_FOLDER_AGG_CONCURRENCY`).
	const folderFetchTasks = [
		() =>
			loomFoldersSearchQuery({
				variables: {
					...queryVariables,
					includeDefaultFolders: true,
				},
				aggAbsoluteUrl: config.aggAbsoluteUrl,
				headers,
			}),
		...spaceAris.map(
			(spaceId) => () =>
				loomFoldersSearchQuery({
					variables: {
						...queryVariables,
						spaceId,
						includeDefaultFolders: false,
					},
					aggAbsoluteUrl: config.aggAbsoluteUrl,
					headers,
				}),
		),
	];

	const responses = await runWithConcurrencyLimit(folderFetchTasks, LOOM_FOLDER_AGG_CONCURRENCY);

	const seenFolderIds = new Set<string>();
	const folderOptions: SelectFilterOption[] = [];

	for (const response of responses) {
		if (response === undefined) {
			continue;
		}
		for (const folder of response.data?.loom_foldersSearch ?? []) {
			if (seenFolderIds.has(folder.id)) {
				continue;
			}
			seenFolderIds.add(folder.id);
			folderOptions.push(mapFolderNodeToOption(folder));
		}
	}

	return folderOptions;
}

const getInitialOptions = async (config: CloudConfig): Promise<SelectFilterOption[]> =>
	fetchLoomFolderOptions(config, { query: '', limit: DEFAULT_LOOM_FOLDER_INITIAL_LIMIT });

const fetchLookupOptions = async (
	input: string,
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	if (!isLoomFolderProduct(config.product)) {
		return [];
	}
	return fetchLoomFolderOptions(config, {
		query: input,
		limit: DEFAULT_LOOM_FOLDER_LOOKUP_LIMIT,
	});
};

const getFilterProducts = (): ProductKey[] => (fg('loom-rovo-search') ? [ProductKeys.Loom] : []);

const baseFilter = createMultiSelectFilter({
	id: 'folder',
	products: getFilterProducts(),
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'folders',
	},
	initialOptions: {
		fetcher: getInitialOptions,
	},
	lookup: {
		fetcher: fetchLookupOptions,
	},
});

const canFetchLoomFoldersForConfig = (config: CloudConfig): boolean =>
	isLoomFolderProduct(config.product) && Boolean(config.cloudId);

const withLoomFolderScopedLoading = (
	monad: SelectFilterMonad<'folder'>,
	config: CloudConfig,
): SelectFilterMonad<'folder'> => ({
	...monad,
	loadInitialOptions: async (onStateChange) => {
		if (!canFetchLoomFoldersForConfig(config)) {
			return monad;
		}
		return monad.loadInitialOptions(onStateChange);
	},
	setLookupInput: async (query, onStateChange) => {
		if (!canFetchLoomFoldersForConfig(config)) {
			return monad;
		}
		return monad.setLookupInput(query, onStateChange);
	},
});

export const filter: Readonly<{
	id: 'folder';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'folder'>;
	};
	products: ProductKey[];
	universal: boolean;
	customValueFields?: string[];
	withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'folder'>;
	getInitialState: (config: CloudConfig) => SelectFilterMonad<'folder'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = {
	...baseFilter,
	withState: (state, config) =>
		withLoomFolderScopedLoading(baseFilter.withState(state, config), config),
	getInitialState: (config) =>
		withLoomFolderScopedLoading(baseFilter.getInitialState(config), config),
	get products() {
		return getFilterProducts();
	},
};
